import { QueryHookOptions } from '@apollo/client';
import { useQuery } from '@ts-gql/apollo';
import { OperationData, OperationVariables, gql } from '@ts-gql/tag';
import React from 'react';

import type { ActivityTargetValue } from '../components/activities/activities-types';

export const GET_ACTIVITIES_QUERY = gql`
  query GetActivities($id: String!, $targetType: ActivityTargetType!) {
    getActivities(input: { id: $id, targetType: $targetType }) {
      cloudId
      targetObject
      targetType
      initiatedByUserId
      initiatedOn
      activityCategory
      propertyName
      propertyType
      propertyOld
      propertyNew
    }
  }
` as import('../../__generated__/ts-gql/GetActivities').type;

export type GetActivitiesQueryVariables = OperationVariables<
  typeof GET_ACTIVITIES_QUERY
>;
export type GetActivitiesQueryResult = OperationData<
  typeof GET_ACTIVITIES_QUERY
>;

export type GetActivityItem = NonNullable<
  ReturnType<typeof processData>
>[number];

export const useGetActivities = (
  options: QueryHookOptions<
    GetActivitiesQueryResult,
    GetActivitiesQueryVariables
  >
) => {
  const variables = options.variables as GetActivitiesQueryVariables;

  const res = useQuery(GET_ACTIVITIES_QUERY, {
    context: { service: 'core' },
    variables,
    ...options,
  });

  return React.useMemo(() => {
    const { data } = res;
    return {
      ...res,
      data: data ? processData(data) : null,
    };
  }, [res]);
};

const processData = (data: GetActivitiesQueryResult) => {
  const activities = data.getActivities;
  const result = activities?.map((activity) => {
    return {
      ...activity,
      targetType:
        (activity.targetType as ActivityTargetValue) ??
        (0 as ActivityTargetValue),
      initiatedOn: new Date(activity.initiatedOn),
      activityCategory: activity.activityCategory ?? 0,
      propertyType: activity.propertyType,
    };
  });

  return result;
};
